import React, { useState } from 'react';
import { Gift, Coffee, MapPin, Truck, Heart, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GiftCertificates: React.FC = () => {
  const [showModal, setShowModal] = useState(false);

  const handleOrderClick = () => {
    setShowModal(true);
  };

  const benefits = [
    {
      icon: Coffee,
      title: '3-Month Subscription',
      description: 'One 12oz bag delivered monthly for 3 months (3 bags total)'
    },
    {
      icon: Heart,
      title: 'Thoughtfully Curated',
      description: 'Each selection tells a Marblehead story'
    },
    {
      icon: Gift,
      title: 'Perfect Gift',
      description: 'Beautiful presentation, meaningful impact'
    },
    {
      icon: Truck,
      title: 'Hassle-Free Delivery',
      description: 'We handle everything, they enjoy the coffee'
    }
  ];

  const features = [
    'Choice of ground or whole bean',
    'Flexible delivery schedule',
    'Personal gift message included',
    'Cancel or modify anytime',
    'Support local Marblehead roasters',
    'Every bag tells a story'
  ];

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#132947] via-[#1a3456] to-[#132947]" />
        <div className="absolute top-20 left-10 text-[#fda36b]/10 text-6xl">☕</div>
        <div className="absolute top-40 right-20 text-[#fda36b]/10 text-8xl">🎁</div>
        <div className="absolute bottom-20 left-1/4 text-[#fda36b]/10 text-5xl">❤️</div>

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Content */}
            <div className="text-center lg:text-left space-y-8">
              <div className="inline-block bg-[#fda36b]/10 px-4 py-2 rounded-full border border-[#fda36b]/20">
                <span className="text-[#fda36b] font-semibold text-sm uppercase tracking-wider">
                  The Perfect Gift
                </span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
                Give the Gift of
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#fda36b] to-[#fda36b]/70 mt-2">
                  Great Coffee
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                A 3-month coffee subscription — one 12oz bag delivered monthly for a total of 3 bags.
              </p>

              <div className="space-y-4 text-lg text-white/80">
                <p className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#fda36b] flex-shrink-0 mt-1" />
                  <span>One 12oz bag per month for 3 months (3 bags total)</span>
                </p>
                <p className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#fda36b] flex-shrink-0 mt-1" />
                  <span>Recipient chooses ground or whole bean</span>
                </p>
                <p className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#fda36b] flex-shrink-0 mt-1" />
                  <span>Gift details collected at checkout</span>
                </p>
              </div>

              <button
                onClick={handleOrderClick}
                className="group relative bg-gradient-to-r from-[#fda36b] to-[#fda36b]/90 text-[#132947] px-12 py-6 rounded-xl font-bold text-xl hover:from-[#fda36b]/90 hover:to-[#fda36b]/80 transition-all duration-300 inline-flex items-center justify-center gap-3 shadow-2xl shadow-[#fda36b]/40 transform hover:scale-105 hover:shadow-[#fda36b]/60"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 rounded-xl" />
                <Gift className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                Order Gift Certificate
              </button>
            </div>

            {/* Right side - Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#fda36b]/20 to-transparent rounded-3xl blur-3xl" />
              <div className="relative bg-white/5 p-8 rounded-3xl border border-white/10 backdrop-blur-sm">
                <img
                  src="/Generated Image November 21, 2025 - 9_32AM.png"
                  alt="Bond Coffee Gift Certificate"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gradient-to-br from-[#1a3456] to-[#132947] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-8">
            Why Choose Bond Coffee Gift Certificates?
          </h2>
          <p className="text-xl text-white/80 text-center mb-12 max-w-3xl mx-auto">
            Each gift certificate is a 3-month subscription — one 12oz bag delivered monthly for 3 total bags of premium coffee.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white/5 p-8 rounded-2xl border border-white/10 hover:border-[#fda36b]/30 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#fda36b] to-[#fda36b]/70 rounded-2xl flex items-center justify-center mb-6">
                  <benefit.icon className="w-8 h-8 text-[#132947]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-[#132947] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            How It Works
          </h2>

          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#fda36b] to-[#fda36b]/70 rounded-full flex items-center justify-center text-[#132947] font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Choose Your Option</h3>
                <p className="text-white/80 text-lg leading-relaxed">
                  Select either Marblehead delivery ($50) or non-Marblehead delivery ($70) for a 3-month subscription
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#fda36b] to-[#fda36b]/70 rounded-full flex items-center justify-center text-[#132947] font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Provide Gift Details</h3>
                <p className="text-white/80 text-lg leading-relaxed">
                  During checkout, tell us about the recipient: their name, address, delivery preferences, and whether they prefer ground or whole bean
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#fda36b] to-[#fda36b]/70 rounded-full flex items-center justify-center text-[#132947] font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">We Handle Everything</h3>
                <p className="text-white/80 text-lg leading-relaxed">
                  Your recipient receives one 12oz bag per month for 3 months (3 total bags). They can enjoy exceptional coffee while you get all the credit for the perfect gift!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What's Included */}
      <div className="bg-gradient-to-br from-[#1a3456] to-[#132947] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-8">
            What's Included
          </h2>
          <p className="text-xl text-white/80 text-center mb-12 max-w-3xl mx-auto">
            Every gift certificate includes everything they need for an exceptional coffee experience
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-4 bg-white/5 p-6 rounded-xl border border-white/10"
              >
                <Check className="w-6 h-6 text-[#fda36b] flex-shrink-0" />
                <span className="text-white/90 text-lg">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-[#132947] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            Choose Your Gift Option
          </h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Marblehead Option */}
            <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-8 rounded-3xl border-2 border-[#fda36b]/30 hover:border-[#fda36b]/50 transition-all duration-300 hover:transform hover:scale-105">
              <div className="text-center mb-6">
                <MapPin className="w-12 h-12 text-[#fda36b] mx-auto mb-4" />
                <h3 className="text-3xl font-bold text-white mb-2">
                  Marblehead Delivery
                </h3>
                <div className="text-5xl font-bold text-[#fda36b] mb-4">
                  $50
                </div>
                <p className="text-white/70">
                  3-month subscription (1 bag/month)
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white/90">
                  <Check className="w-5 h-5 text-[#fda36b] flex-shrink-0" />
                  <span>Free local Marblehead delivery</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <Check className="w-5 h-5 text-[#fda36b] flex-shrink-0" />
                  <span>Support your local roaster</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <Check className="w-5 h-5 text-[#fda36b] flex-shrink-0" />
                  <span>Personal delivery touch</span>
                </li>
              </ul>

              <a
                href="https://buy.stripe.com/5kQ28sgubfttgAt6ZI3VC0c"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-[#fda36b] to-[#fda36b]/90 text-[#132947] px-8 py-4 rounded-xl font-bold text-lg hover:from-[#fda36b]/90 hover:to-[#fda36b]/80 transition-all duration-300 text-center"
              >
                Order Marblehead Gift
              </a>
            </div>

            {/* Non-Marblehead Option */}
            <div className="bg-white/5 p-8 rounded-3xl border-2 border-white/10 hover:border-[#fda36b]/30 transition-all duration-300 hover:transform hover:scale-105">
              <div className="text-center mb-6">
                <Truck className="w-12 h-12 text-[#fda36b] mx-auto mb-4" />
                <h3 className="text-3xl font-bold text-white mb-2">
                  Nationwide Shipping
                </h3>
                <div className="text-5xl font-bold text-white mb-4">
                  $70
                </div>
                <p className="text-white/70">
                  3-month subscription (1 bag/month)
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white/90">
                  <Check className="w-5 h-5 text-[#fda36b] flex-shrink-0" />
                  <span>Ships anywhere in the US</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <Check className="w-5 h-5 text-[#fda36b] flex-shrink-0" />
                  <span>Carefully packaged for freshness</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <Check className="w-5 h-5 text-[#fda36b] flex-shrink-0" />
                  <span>Reliable delivery service</span>
                </li>
              </ul>

              <a
                href="https://buy.stripe.com/5kQ28sgubdll2JDdo63VC0d"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-white/10 text-white border-2 border-white/20 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/20 hover:border-[#fda36b]/50 transition-all duration-300 text-center"
              >
                Order Shipping Gift
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-[#1a3456] to-[#132947] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Give More Than Coffee
          </h2>
          <p className="text-xl md:text-2xl text-white/80 mb-10 leading-relaxed">
            Give moments of joy, stories of Marblehead, and the warmth of knowing you chose the perfect gift.
          </p>
          <button
            onClick={handleOrderClick}
            className="group relative bg-gradient-to-r from-[#fda36b] to-[#fda36b]/90 text-[#132947] px-12 py-6 rounded-xl font-bold text-xl hover:from-[#fda36b]/90 hover:to-[#fda36b]/80 transition-all duration-300 inline-flex items-center justify-center gap-3 shadow-2xl shadow-[#fda36b]/40 transform hover:scale-105 hover:shadow-[#fda36b]/60"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 rounded-xl" />
            <Gift className="w-6 h-6 group-hover:rotate-12 transition-transform" />
            Order Your Gift Certificate
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#132947] rounded-2xl max-w-2xl w-full p-8 border border-[#fda36b]/20">
            <h3 className="text-3xl font-bold text-white mb-6 text-center">
              Choose Your Gift Option
            </h3>

            <div className="space-y-4 mb-8">
              <a
                href="https://buy.stripe.com/5kQ28sgubfttgAt6ZI3VC0c"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-[#fda36b] to-[#fda36b]/90 text-[#132947] p-6 rounded-xl font-bold text-lg hover:from-[#fda36b]/90 hover:to-[#fda36b]/80 transition-all duration-300 text-center"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <div className="text-xl mb-1">Marblehead Delivery</div>
                    <div className="text-sm font-normal opacity-80">3-month subscription • 1 bag delivered monthly</div>
                  </div>
                  <div className="text-3xl font-bold">$50</div>
                </div>
              </a>

              <a
                href="https://buy.stripe.com/5kQ28sgubdll2JDdo63VC0d"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-white/10 text-white border-2 border-white/20 p-6 rounded-xl font-bold text-lg hover:bg-white/20 hover:border-[#fda36b]/50 transition-all duration-300 text-center"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <div className="text-xl mb-1">Nationwide Shipping</div>
                    <div className="text-sm font-normal opacity-80">3-month subscription • 1 bag shipped monthly</div>
                  </div>
                  <div className="text-3xl font-bold">$70</div>
                </div>
              </a>
            </div>

            <p className="text-white/70 text-center mb-6">
              You'll provide recipient details and preferences during Stripe checkout.
            </p>

            <button
              onClick={() => setShowModal(false)}
              className="w-full bg-white/5 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GiftCertificates;
